import type { Treaty } from "@elysiajs/eden";

import {
	computed, defineAsyncComponent, ref,
} from "vue";

import { defineStore } from "pinia";
import { useRouter } from "vue-router";

import { authClient, client } from "@/api";

interface MemberFleet {
	id: string; name: string; access: "aggregate" | "full"; role: "admin" | "pilot"
}

type CurrentSessionGet = ReturnType<typeof authClient.api.v1.sessions._current.get>;
type SessionUser = NonNullable<Treaty.Data<CurrentSessionGet>>["user"];

const initialUrl = () => {
	const path = window.location.pathname;
	const search = window.location.search;
	return path + search;
};

// Where to land after login: the URL the user originally opened — unless that was the
// login page itself (e.g. a refresh while on /login), which would bounce a fresh
// session right back to the login form.
const rawStartPath = initialUrl() || "/";
const startPath = rawStartPath.startsWith("/login") ? "/" : rawStartPath;

export const useAuthStore = defineStore("auth", () => {
	const session = ref<null | SessionUser>(null);
	const loginErr = ref("");
	const checking = ref(false);
	const myFleets = ref<MemberFleet[] | null>(null);
	const router = useRouter();

	let refreshTried = false;
	const loadMyFleets = async () => {
		if (isAdmin.value) {
			myFleets.value = [];
			return;
		}
		try {
			const res = await client.api.v1.sms.myfleets.get();
			let fleets = (res.data?.fleets ?? []) as MemberFleet[];
			// A session issued before the user's first fleet grant lacks the derived
			// foqa scope; one silent re-issue picks it up without a re-login.
			if (fleets.length === 0 && !refreshTried) {
				refreshTried = true;
				const refreshed = await authClient.api.v1.sessions._refresh.post();
				if (refreshed.data?.user) {
					session.value = refreshed.data.user as SessionUser;
					const retry = await client.api.v1.sms.myfleets.get();
					fleets = (retry.data?.fleets ?? []) as MemberFleet[];
				}
			}
			myFleets.value = fleets;
		} catch {
			myFleets.value = [];
		}
	};

	const login = async ({ email, password }: { email: string, password: string }) => {
		loginErr.value = "";
		const res = await authClient.api.v1.sessions.post({ email, password });
		if (res.status !== 200) {
			const errMap = {
				401: "Invalid login credentials",
				500: "An unknown error occurred",
			};
			loginErr.value = errMap[res.status as keyof typeof errMap] || "An unknown error occurred";
		}
		if (res.data?.user) {
			session.value = res.data.user as SessionUser;
			await loadMyFleets();
			router.push(startPath);
		}
	};

	const isPublicRoute = () => {
		// Invite-token account setup is the only sessionless page.
		return window.location.pathname === "/setup-account";
	};

	// Kept out of isPublicRoute: the setup flow depends on that predicate meaning "the sessionless
	// page", and this one must win even for a signed-in staff user following the email link.
	const isOpenInAppRoute = () => window.location.pathname.startsWith("/open/flights/");

	// The SMS web console is ClassA-staff-only. A session without the sms_admin
	// scope is treated as non-admin (missing scopes => no access).
	const isAdmin = computed(() => {
		const scopes = session.value?.scopes;
		return Array.isArray(scopes) && scopes.includes("sms_admin");
	});
	const isMember = computed(() => !isAdmin.value && (myFleets.value?.length ?? 0) > 0);
	const adminFleets = computed(() => (myFleets.value ?? []).filter((f) => f.role === "admin"));
	const isCohortAdmin = computed(() => adminFleets.value.length > 0);

	const rootComponent = computed(() => {
		if (isOpenInAppRoute()) {
			return defineAsyncComponent(() => import("../views/OpenInAppView.vue"));
		}
		if (!session.value && isPublicRoute()) {
			return defineAsyncComponent(() => import("../views/AccountSetupView.vue"));
		}
		if (session.value?.email || isPublicRoute()) {
			if (isAdmin.value) {
				return defineAsyncComponent(() => import("../views/AuthenticatedView.vue"));
			}
			if (myFleets.value === null) {
				return null;
			}
			if (isMember.value) {
				return defineAsyncComponent(() => import("../views/MemberView.vue"));
			}
			return defineAsyncComponent(() => import("../views/NoAccessView.vue"));
		}
		if (!session.value && !checking.value) {
			const LoginView = defineAsyncComponent(() =>
				import("../views/LoginView.vue"),
			);
			return LoginView;
		}
		return null;
	});


	const getCurrentSession = async () => {
		if (isPublicRoute() || isOpenInAppRoute()) {
			return;
		}
		checking.value = true;
		const res = await authClient.api.v1.sessions._current.get();
		if (res.status === 200 && res.data?.user) {
			session.value = res.data.user as SessionUser;
			await loadMyFleets();
			router.push(startPath);
		} else {
			router.push("/login");
		}
		checking.value = false;
	};

	const logout = async () => {
		session.value = null;
		await authClient.api.v1.sessions._current.delete();
		try {
			window.postMessage({ type: "classa-extension-logout" }, window.location.origin);
		} catch {
			// ignore
		}
	};
	return {
		getCurrentSession,
		isAdmin,
		isMember,
		isCohortAdmin,
		adminFleets,
		myFleets,
		login,
		logout,
		rootComponent,
		session,
		loginErr,
	};
});
